// frontend/src/utils/derivedStats.js
//
// Centralized derived-stat computation for Character Sheets and session play.
// Schema supported (mechanics_json schema_version 1):
//  derived.max_hp.add / derived.max_hp.mul
//  derived.movement.add / derived.movement.mul
//  derived.initiative_bonus.add
//  derived.evasion_bonus.add

export function toCode(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

export function safeParseJson(v, fallback = null) {
  try {
    if (v === null || v === undefined) return fallback;
    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return fallback;
      return JSON.parse(s);
    }
    if (typeof v === 'object') return v;
    return fallback;
  } catch {
    return fallback;
  }
}

export function readNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function roundToNearest5(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x / 5) * 5;
}

export function skillTierToDie(tier) {
  const t = Number.isFinite(Number(tier)) ? Number(tier) : 0;
  if (t <= 0) return 'd4';
  if (t === 1) return 'd6';
  if (t === 2) return 'd8';
  if (t === 3) return 'd10';
  if (t === 4) return 'd12';
  return 'd20';
}

function normalizeMechanics(mechanics) {
  if (!mechanics) return null;
  const m = typeof mechanics === 'string' ? safeParseJson(mechanics, null) : mechanics;
  if (!m || typeof m !== 'object') return null;
  if (m.schema_version && Number(m.schema_version) !== 1) {
    // Future-proof: ignore unknown versions rather than crashing.
    return null;
  }
  return m;
}

function isPassiveTierOrOwned(ownedType, tierType) {
  const o = String(ownedType || '').toLowerCase();
  const t = String(tierType || '').toLowerCase();
  return o.includes('passive') || t.includes('passive');
}

function getTierType(tierDef) {
  return String(tierDef?.type || '').toLowerCase();
}

function accumulateDerived(acc, mechanics, sourceLabel) {
  const m = normalizeMechanics(mechanics);
  if (!m) return;

  const d = m.derived || null;
  if (!d || typeof d !== 'object') return;

  // max_hp
  if (d.max_hp && typeof d.max_hp === 'object') {
    const add = readNum(d.max_hp.add, 0);
    const mul = d.max_hp.mul != null ? readNum(d.max_hp.mul, 1) : 1;

    if (add) acc.maxHpAdd += add;
    if (mul !== 1) acc.maxHpMul *= mul;

    if (acc.breakdown) {
      const changes = [];
      if (add) changes.push(`max_hp +${add}`);
      if (mul !== 1) changes.push(`max_hp ×${mul}`);
      if (changes.length) acc.sources.push({ source: sourceLabel, changes });
    }
  }

  // movement
  if (d.movement && typeof d.movement === 'object') {
    const add = readNum(d.movement.add, 0);
    const mul = d.movement.mul != null ? readNum(d.movement.mul, 1) : 1;

    if (add) acc.movementAdd += add;
    if (mul !== 1) acc.movementMul *= mul;

    if (acc.breakdown) {
      const changes = [];
      if (add) changes.push(`movement +${add}`);
      if (mul !== 1) changes.push(`movement ×${mul}`);
      if (changes.length) acc.sources.push({ source: sourceLabel, changes });
    }
  }

  // initiative_bonus
  if (d.initiative_bonus && typeof d.initiative_bonus === 'object') {
    const add = readNum(d.initiative_bonus.add, 0);
    if (add) {
      acc.initiativeBonusAdd += add;
      if (acc.breakdown) acc.sources.push({ source: sourceLabel, changes: [`initiative_bonus +${add}`] });
    }
  }

  // evasion_bonus
  if (d.evasion_bonus && typeof d.evasion_bonus === 'object') {
    const add = readNum(d.evasion_bonus.add, 0);
    if (add) {
      acc.evasionBonusAdd += add;
      if (acc.breakdown) acc.sources.push({ source: sourceLabel, changes: [`evasion_bonus +${add}`] });
    }
  }
}

/**
 * Build ability lookups from API payload:
 * {
 *   byCode: Map<string, abilityDef>,
 *   byName: Map<string, abilityDef> (lowercase name)
 * }
 */
export function buildAbilityLookups(abilitiesList) {
  const byCode = new Map();
  const byName = new Map();

  for (const a of abilitiesList || []) {
    const code = (a?.code || '').toString().trim();
    const name = (a?.name || '').toString().trim();
    if (code) byCode.set(code, a);
    if (name) byName.set(name.toLowerCase(), a);
  }

  return { byCode, byName };
}

/**
 * Compute derived combat stats.
 *
 * Inputs:
 * - stats: { physique, coordination, perception, resilience }
 * - feats: skills object (used for evasion die rule)
 * - forges: owned abilities object (name->data) where data includes { tier, type, code? }
 * - abilityByCode, abilityByName: Maps from buildAbilityLookups()
 *
 * Output:
 * {
 *   base: {...},
 *   mods: {...},
 *   final: { maxHp, movement, initiative, evasion },
 *   breakdown?: { sources: [...] }
 * }
 */
export function computeDerivedCombatStats({
  stats,
  feats,
  forges,
  abilityByCode,
  abilityByName,
  includeBreakdown = false,
}) {
  const phy = readNum(stats?.physique, 1) || 1;
  const coord = readNum(stats?.coordination, 1) || 1;
  const perc = readNum(stats?.perception, 1) || 1;
  const res = readNum(stats?.resilience, 1) || 1;

  // Physique Mod = (Physique - 1)
  const physiqueMod = phy - 1;

  // Base formulas
  const baseMaxHp = (res * 8) + (physiqueMod * 2);
  const baseMovementRaw = (phy + coord) * 3;
  const baseInitiativeBonus = coord + perc;

  // Evasion die rule
  const evasionSkill = feats?.Evasion || feats?.evasion || null;
  const evasionTier = readNum(evasionSkill?.tier, 0) || 0;
  const evasionDie = evasionTier > 0 ? skillTierToDie(evasionTier) : 'd4';

  // Accumulator
  const acc = {
    maxHpAdd: 0,
    maxHpMul: 1,
    movementAdd: 0,
    movementMul: 1,
    initiativeBonusAdd: 0,
    evasionBonusAdd: 0,

    breakdown: includeBreakdown,
    sources: includeBreakdown ? [] : null,
  };

  // Apply passive tier mechanics
  if (abilityByCode && abilityByName) {
    for (const [ownedName, owned] of Object.entries(forges || {})) {
      const ownedTier = readNum(owned?.tier, 1) || 1;

      const ownedCode = String(owned?.code || '').trim();
      const def =
        (ownedCode && abilityByCode.get(ownedCode)) ||
        abilityByName.get(String(ownedName || '').toLowerCase()) ||
        abilityByCode.get(toCode(ownedName));

      if (!def) continue;

      const tierDef = (def.tiers || []).find((t) => readNum(t?.tier, 0) === ownedTier);
      if (!tierDef) continue;

      const tierType = getTierType(tierDef);
      if (!isPassiveTierOrOwned(owned?.type, tierType)) continue;

      const label = `${def.name || ownedName} (Tier ${ownedTier})`;
      accumulateDerived(acc, tierDef.mechanics_json, label);
    }
  }

  // Final calculations
  const maxHpBeforeMul = baseMaxHp + acc.maxHpAdd;
  const finalMaxHp = Math.round(maxHpBeforeMul * acc.maxHpMul);

  const movementRawWithMods = (baseMovementRaw * acc.movementMul) + acc.movementAdd;
  const finalMovement = roundToNearest5(movementRawWithMods);

  const finalInitiativeBonus = baseInitiativeBonus + acc.initiativeBonusAdd;
  const finalInitiative = `1d4 + ${finalInitiativeBonus}`;

  const finalEvasion =
    `${evasionDie}` + (acc.evasionBonusAdd ? ` + ${acc.evasionBonusAdd}` : '');

  const result = {
    base: {
      maxHp: baseMaxHp,
      movementRaw: baseMovementRaw,
      initiativeBonus: baseInitiativeBonus,
      evasionDie,
    },
    mods: {
      maxHpAdd: acc.maxHpAdd,
      maxHpMul: acc.maxHpMul,
      movementAdd: acc.movementAdd,
      movementMul: acc.movementMul,
      initiativeBonusAdd: acc.initiativeBonusAdd,
      evasionBonusAdd: acc.evasionBonusAdd,
    },
    final: {
      maxHp: finalMaxHp,
      movement: finalMovement,
      initiative: finalInitiative,
      evasion: finalEvasion,
    },
  };

  if (includeBreakdown) {
    result.breakdown = acc.sources || [];
  }

  return result;
}
